import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import DynamicFormRenderer from "../components/forms/DynamicFormRenderer";
import { ComponentErrorBoundary } from "../components/ErrorBoundary";
import { useProjects } from "../hooks/useApi";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorDisplay from "../components/ui/ErrorDisplay";

/**
 * ProjectDetail Component
 *
 * Displays detailed information for a specific project by ID.
 * Supports viewing and editing project details using the DynamicFormRenderer.
 */
function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Use the new API hook for projects
  const {
    items: allProjects,
    loading,
    error,
    updateItem,
    deleteItem,
    refresh,
    clearError,
  } = useProjects();

  // Find the current project
  const project = allProjects.find((p) => p.id === id);

  // Check if we're still loading or if project doesn't exist
  const isProjectLoading = loading || (!project && allProjects.length === 0);
  const projectNotFound = !loading && allProjects.length > 0 && !project;

  // Transform project data from API format to form format
  const transformProjectToFormData = (project) => {
    if (!project) return {};

    const formData = {
      // Basic Information - using field names (underscores) not field IDs (hyphens)
      project_name: project.name || "",
      project_type: mapProjectType(project.type),
      project_status: project.status || "",
      project_description: project.description || "",
      start_date: project.timeline?.startDate || "",
      target_completion_date: project.timeline?.expectedCompletion || "",

      // Location & Address - using field names (underscores) not field IDs (hyphens)
      address_line_1: project.location?.address || "",
      address_line_2: "", // Not in current API structure
      city: extractCityFromAddress(project.location?.address) || "",
      postcode: project.location?.postcode || "",
      property_type: mapPropertyType(project.type, project.description),
      site_area: "", // Not in current API structure

      // Financial Information - using field names (underscores)
      total_budget: project.financial?.totalCost
        ? project.financial.totalCost.toString()
        : "",
      land_cost: "", // Not in current API structure
      construction_cost: project.financial?.totalCost
        ? project.financial.totalCost.toString()
        : "",
      expected_profit: project.financial?.profit
        ? project.financial.profit.toString()
        : "",
      profit_margin: project.financial?.profitMargin
        ? project.financial.profitMargin.toString()
        : "",

      // Key Contacts - using field names (underscores)
      developer_company: project.developer || "",
      main_contractor: project.contacts?.contractor || "",
      architect: project.contacts?.architect || "",
      project_manager: project.contacts?.projectManager || "",

      // Include linked valuations for the form renderer
      linkedValuations: project.linkedValuations || [],
    };

    return formData;
  };

  // Transform form data back to API format
  const transformFormDataToProject = (formData) => {
    return {
      name: formData.project_name || "",
      description: formData.project_description || "",
      status: formData.project_status || "",
      type: formData.project_type || "",
      developer: formData.developer_company || "",
      location: {
        address: formData.address_line_1 || "",
        postcode: formData.postcode || "",
      },
      financial: {
        totalCost: parseFloat(formData.total_budget) || 0,
        profit: parseFloat(formData.expected_profit) || 0,
        profitMargin: parseFloat(formData.profit_margin) || 0,
      },
      timeline: {
        startDate: formData.start_date || "",
        expectedCompletion: formData.target_completion_date || "",
      },
      contacts: {
        projectManager: formData.project_manager || "",
        architect: formData.architect || "",
        contractor: formData.main_contractor || "",
      },
      linkedValuations: formData.linkedValuations || [],
    };
  };

  // Helper function to extract city from address
  const extractCityFromAddress = (address) => {
    if (!address) return "";
    // For "Riverside Drive, Manchester" format, extract city after comma
    const parts = address.split(",");
    if (parts.length > 1) {
      return parts[parts.length - 1].trim();
    }
    // If no comma, try to extract from known patterns
    const addressLower = address.toLowerCase();
    if (addressLower.includes("manchester")) return "Manchester";
    if (addressLower.includes("birmingham")) return "Birmingham";
    if (addressLower.includes("london")) return "London";
    if (addressLower.includes("bath")) return "Bath";
    return "";
  };

  // Helper function to map project type to form values
  const mapProjectType = (projectType) => {
    if (!projectType) return "";
    const type = projectType.toLowerCase().trim();

    // Map common project types to form values
    if (
      type.includes("residential") ||
      type.includes("house") ||
      type.includes("apartment")
    )
      return "residential";
    if (type.includes("commercial") || type.includes("office"))
      return "commercial";
    if (type.includes("mixed") || type.includes("development"))
      return "mixed-use";
    if (type.includes("industrial")) return "industrial";
    if (type.includes("retail")) return "retail";
    if (type.includes("hospitality") || type.includes("hotel"))
      return "hospitality";
    if (type.includes("infrastructure")) return "infrastructure";
    if (type.includes("renovation") || type.includes("refurb"))
      return "renovation";

    // Default for generic "Development" types
    if (type.includes("development")) return "residential";

    return "other";
  };

  // Helper function to map project type to property type
  const mapPropertyType = (projectType, description) => {
    if (!projectType && !description) return "";

    const type = (projectType || "").toLowerCase().trim();
    const desc = (description || "").toLowerCase();

    // Check description for clues about property type
    if (desc.includes("townhouse") || desc.includes("terraced"))
      return "terraced";
    if (desc.includes("apartment") || desc.includes("flat")) return "apartment";
    if (desc.includes("detached")) return "detached-house";
    if (desc.includes("semi-detached")) return "semi-detached";
    if (desc.includes("bungalow")) return "bungalow";
    if (desc.includes("maisonette")) return "maisonette";
    if (desc.includes("office")) return "office";
    if (desc.includes("retail")) return "retail";
    if (desc.includes("warehouse") || desc.includes("industrial"))
      return "warehouse";
    if (desc.includes("mixed")) return "mixed-use";
    if (desc.includes("land")) return "land";

    // Fallback to project type mapping
    if (type.includes("residential")) return "apartment";
    if (type.includes("commercial") || type.includes("office")) return "office";
    if (type.includes("retail")) return "retail";
    if (type.includes("industrial")) return "warehouse";
    if (type.includes("mixed")) return "mixed-use";

    return "other";
  };

  // Initialize form data when project loads or changes
  useEffect(() => {
    if (project) {
      const transformedData = transformProjectToFormData(project);

      setFormData(transformedData);
      // Clear any existing validation errors when switching projects
      setValidationErrors({});
    }
  }, [project?.id, project]); // Include project.id to trigger when switching between projects

  // Handle form data changes
  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  // Handle validation changes
  const handleValidationChange = (errors) => {
    setValidationErrors(errors);
  };

  // Handle form save
  const handleFormSave = async (data) => {
    try {
      const transformedData = transformFormDataToProject(data);
      await updateItem(id, transformedData);
      setIsEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone."
      )
    ) {
      try {
        await deleteItem(id);
        navigate("/projects");
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form data
      setFormData(transformProjectToFormData(project));
      setValidationErrors({});
    } else {
      // Start editing - ensure we have fresh data
      setFormData(transformProjectToFormData(project));
    }
    setIsEditing(!isEditing);
  };

  // Handle linked record navigation
  const handleLinkedRecordNavigate = (recordId, recordType) => {
    if (recordType === "valuation") {
      navigate(`/valuations/${recordId}`);
    }
  };

  // Show loading state while data is being fetched
  if (isProjectLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="large" />
        <span className="ml-3 text-gray-600">Loading project details...</span>
      </div>
    );
  }

  // Show error state if project not found
  if (projectNotFound) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Project Not Found
        </h2>
        <p className="text-gray-600 mb-6">
          The project with ID "{id}" could not be found.
        </p>
        <Link
          to="/projects"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Projects
        </Link>
      </div>
    );
  }

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusMap = {
      active: "bg-green-100 text-green-800",
      planning: "bg-yellow-100 text-yellow-800",
      completed: "bg-blue-100 text-blue-800",
      "on-hold": "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
      // Legacy statuses
      concept: "bg-purple-100 text-purple-800",
      approved: "bg-green-100 text-green-800",
      "pre-construction": "bg-yellow-100 text-yellow-800",
      construction: "bg-orange-100 text-orange-800",
      completion: "bg-blue-100 text-blue-800",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800";
  };

  // Get project type label
  const getProjectTypeLabel = (type) => {
    const typeMap = {
      residential: "Residential",
      commercial: "Commercial",
      "mixed-use": "Mixed-Use",
      industrial: "Industrial",
      retail: "Retail",
      office: "Office",
      hospitality: "Hospitality",
      infrastructure: "Infrastructure",
      renovation: "Renovation",
      "student-accommodation": "Student Accommodation",
      other: "Other",
    };
    return typeMap[type] || type;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Render edit form
  if (isEditing) {
    return (
      <ComponentErrorBoundary componentName="ProjectEditForm">
        <div className="space-y-6">
          {/* Form Header */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Link
                to="/projects"
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Projects
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Edit Project
                </h1>
                <p className="text-gray-600 mt-1">
                  Update details for {project.name}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleEditToggle}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800">
                  Project updated successfully!
                </span>
              </div>
            </div>
          )}

          {/* Dynamic Form */}
          <div className="bg-gray-50 rounded-lg p-6">
            {Object.keys(formData).length > 0 ? (
              <DynamicFormRenderer
                templateId="project-basic-v1"
                data={formData}
                onDataChange={handleFormDataChange}
                onSave={handleFormSave}
                onValidationChange={handleValidationChange}
                onLinkedRecordNavigate={handleLinkedRecordNavigate}
                autoSave={true}
                autoSaveInterval={30000}
                showProgress={true}
                className="max-w-6xl"
              />
            ) : (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size="medium" />
                <span className="ml-3 text-gray-600">Loading form data...</span>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {Object.keys(validationErrors).length > 0 && (
                <span className="text-red-600">
                  Please fix {Object.keys(validationErrors).length} validation
                  error(s) before saving
                </span>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleEditToggle}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFormSave(formData)}
                disabled={Object.keys(validationErrors).length > 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Project
              </button>
            </div>
          </div>
        </div>
      </ComponentErrorBoundary>
    );
  }

  // Render project details view
  return (
    <ComponentErrorBoundary componentName="ProjectDetail">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <Link
              to="/projects"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Projects
            </Link>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">
                  {project.name}
                </h1>
                {loading && <LoadingSpinner size="small" />}
                {error && (
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-red-500"
                    title="Error loading data"
                  />
                )}
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(project.status)}`}
                >
                  {project.status?.charAt(0).toUpperCase() +
                    project.status?.slice(1)}
                </span>
                <span className="text-gray-600">
                  {getProjectTypeLabel(project.type)}
                </span>
                <span className="text-gray-600">ID: {project.id}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleEditToggle}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <PencilIcon className="h-4 w-4" />
              <span>Edit</span>
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <ErrorDisplay
            error={error}
            onRetry={refresh}
            onDismiss={clearError}
            type="warning"
            className="mb-4"
          />
        )}

        {/* Success Message */}
        {saveSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">
                Project updated successfully!
              </span>
            </div>
          </div>
        )}

        {/* Project Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Financial Overview
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Budget:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(project.financial?.totalCost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Expected Profit:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(project.financial?.profit)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Profit Margin:</span>
                <span className="text-sm font-medium">
                  {project.financial?.profitMargin
                    ? `${project.financial.profitMargin}%`
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Start Date:</span>
                <span className="text-sm font-medium">
                  {project.timeline?.startDate
                    ? new Date(project.timeline.startDate).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  Expected Completion:
                </span>
                <span className="text-sm font-medium">
                  {project.timeline?.expectedCompletion
                    ? new Date(
                        project.timeline.expectedCompletion
                      ).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Location</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Address:</span>
                <p className="text-sm font-medium mt-1">
                  {project.location?.address || "N/A"}
                </p>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Postcode:</span>
                <span className="text-sm font-medium">
                  {project.location?.postcode || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Project Information using DynamicFormRenderer in read-only mode */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Project Details
          </h2>
          {Object.keys(formData).length > 0 ? (
            <DynamicFormRenderer
              templateId="project-basic-v1"
              fieldsId="project-fields"
              data={formData}
              onLinkedRecordNavigate={handleLinkedRecordNavigate}
              disabled={true}
              showProgress={false}
              className="max-w-6xl"
            />
          ) : (
            <div className="flex justify-center items-center py-8">
              <LoadingSpinner size="medium" />
              <span className="ml-3 text-gray-600">Loading form data...</span>
            </div>
          )}
        </div>
      </div>
    </ComponentErrorBoundary>
  );
}

export default ProjectDetail;
